import { useEffect, useState } from "react";
import axios from "axios";
import { CheckCircle2, AlertTriangle, Radio } from "lucide-react";
import { useLang } from "@/lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const DataSourceStatus = () => {
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/health/services`)
      .then((r) => alive && setData(r.data))
      .catch(() => alive && setData({ services: [] }))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  return (
    <div
      data-testid="service-status-container"
      className="rounded-xl border border-border bg-card p-5"
    >
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Radio className="h-4 w-4 text-accent" />
          <h3 className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
            {t("status.header")}
          </h3>
        </div>
        <span
          data-testid="mock-live-indicator"
          className="rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-600 dark:text-emerald-400"
        >
          {t("status.live")}
        </span>
      </div>

      <div className="space-y-2.5">
        {loading && <p className="text-sm text-muted-foreground">{t("status.probing")}</p>}
        {!loading &&
          (data?.services || []).map((s) => (
            <div
              key={s.key}
              data-testid={`service-${s.key}-status`}
              className="flex items-center justify-between gap-3 rounded-lg border border-border bg-secondary/40 px-3 py-2.5"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  {s.online ? (
                    <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" />
                  ) : (
                    <AlertTriangle className="h-4 w-4 shrink-0 text-destructive" />
                  )}
                  <p className="truncate text-sm font-medium text-foreground">{s.name}</p>
                </div>
                <p className="truncate pl-5.5 font-mono text-[10px] text-muted-foreground">
                  {s.agency}
                  {!s.implemented && t("status.phase2")}
                </p>
              </div>
              <span
                data-testid="service-latency-pill"
                className={`shrink-0 rounded-full px-2 py-0.5 font-mono text-[10px] font-semibold ${
                  s.online
                    ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
                    : "bg-destructive/10 text-destructive"
                }`}
              >
                {s.online ? `${s.latency_ms} ms` : t("status.offline")}
              </span>
            </div>
          ))}
      </div>
    </div>
  );
};
